/**
 * This should use the Data Management (Lexicon++) mechanics to do an event/property rename
 * Currently it is expected that from an app to call fetchDisplayNameData when fetching display names is necessary
 * Otherwise, display names will be mapped from mp.report.globals
 * TODO: In the near future as we bundle event dropdowns into a component, the registry will initialized as part of that
 */

import {getOwnAttr} from 'common/util';
import {
  eventPropertyDefinitionsStore,
  getAllPropertyDefinitionsForGroupProfiles,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';

import {EVENTS, PROPERTIES, ResourceType} from './constants';

/**
 * These projects have a bad implementation, or too many properties, and thus need to have some of their queries disabled
 * for the frontend to work properly.
 */
export const DISABLED_DATA_PROPERTIES_PROJECTS = [
  /**
   * This project is a result of SRFE-3788, and a corresponding slack thread is this: https://mixpanel.slack.com/archives/C9JC7MHTJ/p1631732325312700
   * Essentially, the project has too many properties due to a bad implementation, and thus needs to be disabled in profiles
   * to ensure that the UI loads without blocking on these calls.
   **/
  653121,
];

/**
 * Defensively access mp.report.globals
 */
function getMpReportGlobals() {
  // eslint-disable-next-line no-undef
  // eslint-disable-next-line local-rules/deprecated-member-access
  return (typeof mp === `object` && mp.report?.globals) || {};
}

export function getEventDisplayName(eventName: string): string {
  const def = getOwnAttr(eventDefinitionsStore.value, eventName);

  return (
    (def?.customEventId ? def.name : def?.displayName) ||
    getMpReportGlobals().mapped_events?.[eventName] ||
    getOwnAttr(EVENTS, eventName) ||
    eventName
  );
}

export function getPropertyDisplayName(propertyName: string, resourceType: ResourceType, dataGroupId?: string): string {
  const dataGroupIdMatch =
    dataGroupId &&
    getAllPropertyDefinitionsForGroupProfiles().find(
      (prop) => prop.name === propertyName && prop.dataGroupId === dataGroupId,
    )?.displayName;
  return (
    dataGroupIdMatch ||
    (resourceType === ResourceType.Event &&
      getOwnAttr(eventPropertyDefinitionsStore.value, propertyName)?.displayName) ||
    (resourceType === ResourceType.User &&
      getOwnAttr(peoplePropertyDefinitionsStore.value, propertyName)?.displayName) ||
    getOwnAttr(getMpReportGlobals().mapped_properties || {}, propertyName) ||
    getOwnAttr(PROPERTIES, propertyName) ||
    propertyName
  );
}

/**
 * @deprecated - use getPropertyDisplayName instead
 */
export function getPropertyDisplayNameOld(propertyName: string): string {
  return (
    getOwnAttr(eventPropertyDefinitionsStore.value, propertyName)?.displayName ||
    getOwnAttr(peoplePropertyDefinitionsStore.value, propertyName)?.displayName ||
    getOwnAttr(getMpReportGlobals().mapped_properties || {}, propertyName) ||
    getOwnAttr(PROPERTIES, propertyName) ||
    propertyName
  );
}

export type FetchDisplayNamesReturnType = [void, void, void];

/**
 * Privately scoped promise to ensure that we don't subscribe multiple times
 */
let _fetchDisplayNameDataPromise: Promise<FetchDisplayNamesReturnType> | null = null;

/**
 * Populate stores required for display name mappings.
 * Returns a promise that resolves once the store subscription is fired for each request.
 */
export function fetchDisplayNameData(
  projectId: number | string,
  workspaceId?: number | string,
): Promise<FetchDisplayNamesReturnType> {
  function promisifySubscription(store): Promise<void> {
    return new Promise((resolve) => {
      function resolveAndUnsubscribe() {
        resolve();
        store.unsubscribe(resolveAndUnsubscribe);
      }
      store.subscribe(resolveAndUnsubscribe);
    });
  }

  if (_fetchDisplayNameDataPromise !== null) {
    return _fetchDisplayNameDataPromise;
  }

  eventPropertyDefinitionsStore.fetch({projectId, workspaceId, category: `event`});
  peoplePropertyDefinitionsStore.fetch({projectId, workspaceId, category: `user`});
  eventDefinitionsStore.fetch({projectId, workspaceId});

  const subscriptionPromise = Promise.all([
    promisifySubscription(eventDefinitionsStore),
    promisifySubscription(eventPropertyDefinitionsStore),
    promisifySubscription(peoplePropertyDefinitionsStore),
  ]);

  return (_fetchDisplayNameDataPromise = subscriptionPromise);
}
